import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AlertTriangle, ChevronLeft, Upload, FileText, 
  Shield, MessageSquare, Package, User, X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  raiseEscrowDispute,
  getDisputeCategories,
  uploadDisputeEvidence
} from '../../services/api';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';

const RaiseDispute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get data from navigation state
  const { 
    escrowId,
    conversationId,
    amount,
    sellerId,
    buyerId,
    listingTitle,
    fromChat = true,
    chatState
  } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    reason: '',
    category: '',
    evidence: []
  });
  
  // Evidence state
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [newEvidence, setNewEvidence] = useState({
    description: '',
    file: null,
    evidence_type: 'image'
  });
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  
  // Determine if user is buyer or seller
  const isBuyer = user?.user_id === buyerId;
  const isSeller = user?.user_id === sellerId;
  const otherPartyName = isBuyer ? 'Seller' : 'Buyer';
  
  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    // Validate required data
    if (!escrowId || !conversationId) {
      setError('Missing required dispute information. Please go back and try again.');
      return;
    }
    
    if (!isBuyer && !isSeller) {
      setError('You are not authorized to raise a dispute for this transaction.');
      return;
    }
    
    loadCategories();
  }, []);
  
  const loadCategories = async () => {
    try {
      const raisedBy = isBuyer ? 'buyer' : 'seller';
      const response = await getDisputeCategories(raisedBy);
      
      if (response.success) {
        setCategories(response.data);
        setFilteredCategories(response.data);
      }
    } catch (error) {
      // console.error('Error loading categories:', error);
    }
  };
  
  const handleBack = () => {
    if (fromChat && chatState) {
      navigate(`/chats/${conversationId}`, { state: chatState });
    } else {
      navigate(-1);
    }
  };
  
  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({ ...prev, category: categoryId }));
    
    // Auto-fill reason based on category if empty
    if (!formData.reason.trim()) {
      const selectedCategory = categories.find(cat => cat.id === categoryId);
      if (selectedCategory) {
        setFormData(prev => ({
          ...prev,
          reason: `Issue with ${selectedCategory.label.toLowerCase()}.`
        }));
      }
    }
  };
  
  const handleAddEvidence = () => {
    if (!newEvidence.file) {
      alert('Please select a file to upload');
      return;
    }
    
    // Create preview object
    const evidenceItem = {
      id: Date.now(),
      file: newEvidence.file,
      description: newEvidence.description,
      type: newEvidence.evidence_type,
      preview: URL.createObjectURL(newEvidence.file)
    };
    
    setEvidenceFiles(prev => [...prev, evidenceItem]);
    setFormData(prev => ({
      ...prev,
      evidence: [...prev.evidence, evidenceItem]
    }));
    
    setNewEvidence({ description: '', file: null, evidence_type: 'image' });
    setShowEvidenceModal(false);
  };
  
  const removeEvidence = (id) => {
    setEvidenceFiles(prev => prev.filter(item => item.id !== id));
    setFormData(prev => ({
      ...prev,
      evidence: prev.evidence.filter(item => item.id !== id)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.reason.trim()) {
      alert('Please describe the reason for your dispute');
      return;
    }
    
    if (!formData.category) {
      alert('Please select a dispute category');
      return;
    }
    
    if (formData.reason.trim().length < 10) {
      alert('Please provide a more detailed description (minimum 10 characters)');
      return;
    }
    
    const confirmed = window.confirm(
      `Are you sure you want to raise a dispute?\n\n` +
      `• Amount: ₦${amount?.toLocaleString()}\n` +
      `• Against: ${otherPartyName}\n` +
      `• Funds will be frozen until resolved\n\n` +
      `This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      setSubmitting(true);
      setError('');
      
      // First, raise the dispute
      const disputeData = {
        user_id: user.user_id,
        reason: formData.reason.trim(),
        category: formData.category
      };
      
      const result = await raiseEscrowDispute(escrowId, disputeData);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to raise dispute');
      }
      
      const disputeId = result.dispute_id;
      
      // Upload evidence files if any
      if (evidenceFiles.length > 0) {
        for (const evidence of evidenceFiles) {
          const formData = new FormData();
          formData.append('file', evidence.file);
          formData.append('dispute_id', disputeId);
          formData.append('uploaded_by', user.user_id);
          formData.append('evidence_type', evidence.type);
          formData.append('description', evidence.description);
          
          await uploadDisputeEvidence(formData);
        }
      }
      
      // Success - navigate to dispute details
      navigate(`/disputes/${disputeId}`, {
        state: {
          fromChat: fromChat,
          conversationId: conversationId,
          chatState: chatState,
          justCreated: true
        }
      });
      
    } catch (error) {
      // console.error('Error raising dispute:', error);
      setError(error.message || 'Failed to raise dispute. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Redirecting to login..." />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={handleBack}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Raise Dispute</h1>
                <p className="text-sm text-gray-600">
                  Secure transaction #{escrowId}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Transaction Summary */}
        <div className="bg-white rounded-xl border shadow-sm mb-6">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-500" />
              Transaction Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Amount
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  {formatPrice(amount)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Item
                </label>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-900">{listingTitle || 'Unknown Item'}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Your Role
                </label>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    isBuyer ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {isBuyer ? 'Buyer' : 'Seller'}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Against
                </label>
                <p className="text-gray-900">{otherPartyName}</p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-yellow-800">Important Notice</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Raising a dispute will freeze the funds in escrow until the issue is resolved. 
                    Please provide clear details and evidence to help us mediate effectively.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Dispute Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                1. Select Issue Type
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategoryChange(category.id)}
                    className={`p-4 border rounded-lg text-left transition-colors ${
                      formData.category === category.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {category.label}
                      </span>
                      {formData.category === category.id && (
                        <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {category.description || 'Select this category'}
                    </p>
                  </button>
                ))}
              </div>
              
              {!formData.category && (
                <p className="mt-3 text-sm text-red-600">
                  Please select an issue type
                </p>
              )}
            </div>
          </div>
          
          {/* Reason Description */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                2. Describe the Issue
              </h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Description *
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={`Describe what happened in detail. Be specific about:\n• What went wrong\n• When it happened\n• What you expected\n• Any communication with ${otherPartyName}`}
                  className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  required
                />
                <div className="flex justify-between mt-2">
                  <p className="text-sm text-gray-500">
                    Minimum 10 characters
                  </p>
                  <p className={`text-sm ${
                    formData.reason.length < 10 ? 'text-red-500' : 'text-green-500'
                  }`}>
                    {formData.reason.length} characters
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-800">Tips for a good description:</p>
                    <ul className="text-sm text-blue-700 mt-1 space-y-1">
                      <li>• Be specific about dates and times</li>
                      <li>• Include any relevant message screenshots</li>
                      <li>• Clearly state what resolution you expect</li>
                      <li>• Remain factual and professional</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Evidence Upload */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  3. Add Evidence (Optional)
                </h2>
                <button
                  type="button"
                  onClick={() => setShowEvidenceModal(true)}
                  className="flex items-center gap-2 text-orange-600 hover:text-orange-700"
                >
                  <Upload className="w-4 h-4" />
                  Add Evidence
                </button>
              </div>
              
              {evidenceFiles.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No evidence added yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Add photos, screenshots, or documents to support your case
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {evidenceFiles.map((evidence) => (
                    <div key={evidence.id} className="relative border rounded-lg overflow-hidden group">
                      {evidence.type === 'image' ? (
                        <img
                          src={evidence.preview}
                          alt="Evidence"
                          className="w-full h-32 object-cover"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                          <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      
                      <button
                        type="button"
                        onClick={() => removeEvidence(evidence.id)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      
                      <div className="p-2">
                        <p className="text-xs text-gray-500 truncate">
                          {evidence.file.name}
                        </p>
                        {evidence.description && (
                          <p className="text-xs text-gray-700 truncate">
                            {evidence.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4">
                <p className="text-sm text-gray-500">
                  Supported: Images (JPG, PNG), PDF, Documents (DOC, DOCX). Max 10MB per file.
                </p>
              </div>
            </div>
          </div>
          
          {/* Submit Section */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="p-6">
              <div className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={submitting}
                    className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  
                  <button
                    type="submit"
                    disabled={submitting || !formData.category || !formData.reason.trim()}
                    className="flex-1 bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Raising Dispute...
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5" />
                        Raise Dispute & Freeze Funds
                      </>
                    )}
                  </button>
                </div>
                
                <p className="text-sm text-gray-500 text-center">
                  By raising a dispute, you agree to our dispute resolution process. 
                  Our support team will review your case within 24-48 hours.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
      
      {/* Evidence Upload Modal */}
      <Modal
        isOpen={showEvidenceModal}
        onClose={() => setShowEvidenceModal(false)}
        title="Add Evidence"
        size="md"
        showCloseButton={true}
        showFooter={true}
        confirmText="Add Evidence"
        cancelText="Cancel"
        onConfirm={handleAddEvidence}
        onCancel={() => setShowEvidenceModal(false)}
        confirmButtonType="primary"
        confirmDisabled={!newEvidence.file}
      >
        <div className="py-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evidence Type
            </label>
            <select
              value={newEvidence.evidence_type}
              onChange={(e) => setNewEvidence(prev => ({ ...prev, evidence_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="image">Image (JPG, PNG, GIF)</option>
              <option value="document">Document (PDF, DOC)</option>
              <option value="message_screenshot">Message Screenshot</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={newEvidence.description}
              onChange={(e) => setNewEvidence(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Briefly describe what this evidence shows..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              rows="3"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              {newEvidence.file ? (
                <div>
                  <p className="text-sm text-gray-700">{newEvidence.file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(newEvidence.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    type="button"
                    onClick={() => setNewEvidence(prev => ({ ...prev, file: null }))}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag & drop or click to select file
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    Max 10MB. Supported: JPG, PNG, GIF, PDF, DOC, DOCX
                  </p>
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-medium text-gray-700">
                    Choose File
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setNewEvidence(prev => ({ ...prev, file }));
                        }
                      }}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Clear evidence helps resolve disputes faster. 
              Examples: photos of damaged items, message screenshots, delivery receipts.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RaiseDispute;